import React, { useState } from "react";
import { Badge, Button, Card, Flex, Text } from "@radix-ui/themes";
import { PermissionAlertDialog, ToastAlert } from "components";
import { useGetWhatsappCredentialsStatus, useSaveWhatsappCredentialAccounts, useValidateWhatsappCredentialAccount } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { AuthWhatsappDialog } from "./AuthWhatsappDialog";
import { DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE, normalizeWhatsappCredentialAccount, serializeWhatsappCredentialAccount } from "./whatsappCredentialsStore";

function buildDefaultFormData() {
  return {
    accountName: "",
    appId: "",
    businessId: "",
    accessToken: "",
    autoReplyEnabled: true,
    autoReplyMessage: DEFAULT_WHATSAPP_AUTO_REPLY_MESSAGE,
  };
}

function toStringValue(value) {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value).trim();
}

function getApiErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.message || error?.response?.data?.error || fallbackMessage;
}

export function BrowseWhatsappCard({ userProfile }) {
  const { setToastMessage } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [formData, setFormData] = useState(buildDefaultFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  const { data: statusData } = useGetWhatsappCredentialsStatus(userProfile?.organisation_id);
  const saveWhatsappCredentialAccounts = useSaveWhatsappCredentialAccounts();
  const validateWhatsappCredentialAccount = useValidateWhatsappCredentialAccount();
  const isWhatsappActive = statusData?.data?.is_active === true;

  const resetDialog = () => {
    setFormData(buildDefaultFormData());
    setFieldErrors({});
    setPendingAccounts([]);
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      resetDialog();
    }
  };

  const handleConnectClick = () => {
    if (userProfile.user_role === "Owner") {
      handleOpenChange(true);
      return;
    }

    setShowPermissionAlert(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((previousData) => ({
      ...previousData,
      [fieldName]: value,
    }));
    setFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
  };

  const handleAddAccount = async () => {
    const nextErrors = {
      accountName: !toStringValue(formData.accountName),
      appId: !toStringValue(formData.appId),
      businessId: !toStringValue(formData.businessId),
      accessToken: !toStringValue(formData.accessToken),
    };

    setFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    setIsAddingAccount(true);
    try {
      await validateWhatsappCredentialAccount.mutateAsync({
        organisation_id: userProfile.organisation_id,
        account_name: formData.accountName,
        app_id: formData.appId,
        business_id: formData.businessId,
        access_token: formData.accessToken,
        auto_reply_enabled: formData.autoReplyEnabled,
        auto_reply_message: formData.autoReplyMessage,
      });
      const nextAccount = normalizeWhatsappCredentialAccount({
        accountName: formData.accountName,
        appId: formData.appId,
        businessId: formData.businessId,
        accessToken: formData.accessToken,
        autoReplyEnabled: formData.autoReplyEnabled,
        autoReplyMessage: formData.autoReplyMessage,
      }, pendingAccounts.length);

      setPendingAccounts((previousAccounts) => {
        const remainingAccounts = previousAccounts.filter((account) => (
          account.appId !== nextAccount.appId || account.businessId !== nextAccount.businessId
        ));
        return [...remainingAccounts, nextAccount];
      });
      setFormData(buildDefaultFormData());
      setFieldErrors({});
      setToastMessage("WhatsApp account validated");
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to validate WhatsApp credentials"));
    } finally {
      setIsAddingAccount(false);
    }
  };

  const handleRemoveAccount = (accountClientKey) => {
    setPendingAccounts((previousAccounts) => previousAccounts.filter((account) => account.clientKey !== accountClientKey));
  };

  const handleContinue = async () => {
    if (pendingAccounts.length === 0) {
      return;
    }

    try {
      await saveWhatsappCredentialAccounts.mutateAsync({
        organisation_id: userProfile.organisation_id,
        accounts: pendingAccounts.map(serializeWhatsappCredentialAccount),
      });
      setToastMessage("WhatsApp credentials saved");
      handleOpenChange(false);
    } catch (error) {
      setToastMessage(getApiErrorMessage(error, "Unable to save WhatsApp credentials right now"));
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          width: "100%",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            height="40px"
            width="40px"
            p="2"
            justify="center"
            align="center"
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img src={require("../../../assets/images/whatsapp.png")} alt="WhatsApp" style={{ width: "24px" }} />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              WhatsApp
            </Text>
            <Text size="1" color="gray">
              CRM & Marketing messages
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isWhatsappActive ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthWhatsappDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={resetDialog}
        onContinue={handleContinue}
        onAddAccount={handleAddAccount}
        onRemoveAccount={handleRemoveAccount}
        accounts={pendingAccounts}
        formData={formData}
        onFieldChange={handleFieldChange}
        fieldErrors={fieldErrors}
        isAddAccountLoading={isAddingAccount}
        isContinueLoading={saveWhatsappCredentialAccounts.isPending}
        isContinueDisabled={pendingAccounts.length === 0}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
      <ToastAlert />
    </>
  );
}
